/**
 * YOJO Accessibility — focus trap + focus return
 * Wave 2 Batch 4A.2 — 2026-04-19
 * Refinement   4A.2.1 — 2026-04-19:
 *   - opener capture no longer depends on rAF timing: we listen for clicks/keydowns
 *     on any likely trigger BEFORE the overlay opens, and stash the trigger as the
 *     opener for the matching overlay. This survives the case where existing code
 *     has already moved focus inside the overlay before our mutation observer fires.
 *   - focus-move-into: retries through a short bounded schedule (next tick → 50ms →
 *     150ms → 400ms) until a visible focusable is available. Bails out silently
 *     if none is ever found.
 *
 * Fully additive. Does NOT modify any existing JS.
 * Attaches focus traps to:
 *   - every <sidebar-drawer> (cart drawer + menu drawer)
 *   - #yojo-search-overlay
 *   - #yojo-newsletter-popup
 *   - modal-box.popup-age-verification (18+ age gate — containment only, ESC stays disabled)
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
    'summary'
  ].join(',');

  function isElementVisible(el) {
    if (!el) return false;
    if (el.offsetParent !== null) return true;
    var cs;
    try { cs = getComputedStyle(el); } catch (e) { return false; }
    // Fixed / absolute elements may report null offsetParent but still be visible.
    if (cs.position === 'fixed' || cs.position === 'absolute') {
      return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0;
    }
    return false;
  }

  function getFocusable(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) {
        if (!isElementVisible(el)) return false;
        if (el.closest('[aria-hidden="true"]')) return false;
        if (el.closest('[inert]')) return false;
        return true;
      }
    );
  }

  // ---------------------------------------------------------------------
  // Opener registry — maps overlay root element -> trigger element.
  // Populated by pre-click / pre-keydown listeners BEFORE the overlay opens,
  // so we never lose the opener to existing code that moves focus first.
  // ---------------------------------------------------------------------
  var pendingOpeners = new WeakMap(); // root -> opener element

  function registerOpenerFromEvent(event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('[aria-controls], [data-open-search], [data-js-sidebar-handle], .site-search-handle, .button--cart-handle')
      : null;
    if (!trigger) return;

    // aria-controls wins: it explicitly names the target.
    var targetId = trigger.getAttribute('aria-controls');
    if (targetId) {
      var targetEl = document.getElementById(targetId);
      if (targetEl) pendingOpeners.set(targetEl, trigger);
    }
    // data-open-search / .site-search-handle → search overlay.
    if (trigger.hasAttribute('data-open-search') || trigger.classList.contains('site-search-handle')) {
      var searchEl = document.getElementById('yojo-search-overlay');
      if (searchEl) pendingOpeners.set(searchEl, trigger);
    }
    // .button--cart-handle (legacy) → cart sidebar.
    if (trigger.classList.contains('button--cart-handle')) {
      var cartEl = document.getElementById('site-cart-sidebar');
      if (cartEl) pendingOpeners.set(cartEl, trigger);
    }
  }

  // a11-F3 (swarm 2026-10-01): the control a keyboard shopper last pressed
  // ("הוספה לסל" on a product page). The cart drawer opens a round trip later,
  // by which time <main> is inert and focus has left that button.
  var lastKeyActivation = null; // { el, t }

  // Capture-phase so we run BEFORE existing click handlers.
  document.addEventListener('click', registerOpenerFromEvent, true);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      registerOpenerFromEvent(e);
      if (e.target && e.target.closest) lastKeyActivation = { el: e.target, t: Date.now() };
    }
  }, true);

  // Focus can go back to `el` when the overlay closes. While the overlay is
  // open, the page behind it is inert by design, so `closing` adds that check
  // only for the moment focus is actually handed back.
  function canTakeFocusBack(el, root, closing) {
    if (!el || el === document.body || typeof el.focus !== 'function' || !document.contains(el)) return false;
    if (root.contains(el) || !isElementVisible(el)) return false;
    return !closing || (!el.closest('[inert]') && !el.closest('[aria-hidden="true"]'));
  }

  // a11-F6 (swarm 2026-10-01): the skip link moves focus INTO <main>. Main is
  // not focusable in the markup: a permanent tabindex="-1" made every mouse
  // click on page text focus main, so the next Tab restarted at the top of
  // the page. It takes tabindex="-1" for this jump only and drops it on blur.
  // The scroll leaves main's top just under the sticky header, so the first
  // Tab stop inside main is not hidden behind it.
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('a.skip-to-content[href="#main"]') : null;
    var main = link ? document.getElementById('main') : null;
    if (!main) return;
    e.preventDefault();
    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
      main.addEventListener('blur', function drop() {
        main.removeAttribute('tabindex');
        main.removeEventListener('blur', drop);
      });
    }
    main.focus({ preventScroll: true });
    var header = document.querySelector(".shopify-section[id$='__header']");
    var pos = header ? getComputedStyle(header).position : '';
    var offset = pos === 'sticky' || pos === 'fixed' ? header.getBoundingClientRect().height : 0;
    window.scrollTo(0, Math.max(0, main.getBoundingClientRect().top + window.pageYOffset - offset));
  });

  // ---------------------------------------------------------------------
  // Bounded retry focus-move: next tick → 50 → 150 → 400 ms. Stops on success.
  // ---------------------------------------------------------------------
  function moveFocusInto(root) {
    var delays = [0, 50, 150, 400];
    function tryOnce(idx) {
      var items = getFocusable(root);
      if (items.length > 0) {
        try { items[0].focus({ preventScroll: true }); } catch (e) { try { items[0].focus(); } catch (_) {} }
        // Verify focus landed; if not, keep retrying (some existing code may steal focus back).
        if (document.activeElement === items[0]) return;
      }
      if (idx + 1 < delays.length) {
        setTimeout(function () { tryOnce(idx + 1); }, delays[idx + 1]);
      }
    }
    tryOnce(0);
  }

  /**
   * Attach a focus trap to one overlay-like element.
   */
  function attachTrap(root, config) {
    if (!root || root.__yojoTrapAttached) return;
    root.__yojoTrapAttached = true;

    var opener = null;
    var lastOpenState = false;
    var preOpenFocus = null; // a11-F3: { el, t } focus at the moment the overlay began to open

    function trapHandler(e) {
      if (e.key !== 'Tab') return;
      var items = getFocusable(root);
      if (items.length === 0) return;
      var first = items[0];
      var last = items[items.length - 1];
      var current = document.activeElement;
      if (e.shiftKey) {
        if (current === first || !root.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (current === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function escHandler(e) {
      if (e.key === 'Escape' && config.isOpen(root)) {
        if (typeof config.close === 'function') {
          config.close(root);
        }
      }
    }

    function resolveOpener() {
      // 1. Pre-registered opener from click/keydown capture.
      var pending = pendingOpeners.get(root);
      if (pending && typeof pending.focus === 'function' && document.contains(pending)) {
        return pending;
      }
      // a11-F3: drawers opened by another control (the product page add
      // button, a card "+") return focus to that control, not to the header
      // cart icon nine stops above it. First the control that held focus when
      // the drawer began to open, then the control last pressed from the
      // keyboard. Pointer taps that never focused anything keep the icon.
      if (config.returnToTrigger) {
        var now = Date.now();
        if (preOpenFocus && now - preOpenFocus.t < 1000 && canTakeFocusBack(preOpenFocus.el, root)) {
          return preOpenFocus.el;
        }
        if (lastKeyActivation && now - lastKeyActivation.t < 10000 && canTakeFocusBack(lastKeyActivation.el, root)) {
          return lastKeyActivation.el;
        }
      }
      // 2. Fallback: any element in the DOM with aria-controls="<root.id>".
      if (root.id) {
        var controller = document.querySelector('[aria-controls="' + root.id + '"]');
        if (controller && typeof controller.focus === 'function') return controller;
      }
      // 3. Last resort: whatever had focus (excluding body).
      if (document.activeElement && document.activeElement !== document.body && !root.contains(document.activeElement)) {
        return document.activeElement;
      }
      return null;
    }

    function onOpen() {
      opener = resolveOpener();
      moveFocusInto(root);
      document.addEventListener('keydown', trapHandler, true);
      document.addEventListener('keydown', escHandler, true);
    }

    function onClose() {
      document.removeEventListener('keydown', trapHandler, true);
      document.removeEventListener('keydown', escHandler, true);
      // Clear the pending-opener slot now that this open/close cycle is done.
      pendingOpeners.delete(root);
      var capturedOpener = opener;
      opener = null;
      preOpenFocus = null;
      // a11-F3: a trigger that cannot take focus back any more (a quick-add
      // sheet that has since closed) hands over to the header control.
      if (config.returnToTrigger && capturedOpener && !canTakeFocusBack(capturedOpener, root, true) && root.id) {
        var controller = document.querySelector('[aria-controls="' + root.id + '"]');
        if (controller && canTakeFocusBack(controller, root, true)) capturedOpener = controller;
      }
      if (!capturedOpener || typeof capturedOpener.focus !== 'function' || !document.contains(capturedOpener)) return;
      // Defer to next tick + retry to survive mid-animation focus steals.
      // The browser sometimes restores focus to the previously-focused-inside element
      // after a class change, so we wait out that restore and then claim focus back.
      var attempts = [0, 30, 120, 320];
      attempts.forEach(function (d) {
        setTimeout(function () {
          if (document.activeElement !== capturedOpener) {
            try { capturedOpener.focus({ preventScroll: true }); } catch (e) {
              try { capturedOpener.focus(); } catch (_) {}
            }
          }
        }, d);
      });
    }

    var mo = new MutationObserver(function () {
      var nowOpen = !!config.isOpen(root);
      // a11-F3: show() unhides the drawer 15 ms before it adds the open class
      // and makes <main> inert. Note what still holds focus at that moment.
      if (config.returnToTrigger && !nowOpen && !lastOpenState) {
        var held = document.activeElement;
        if (held && held !== document.body && !root.contains(held)) preOpenFocus = { el: held, t: Date.now() };
      }
      if (nowOpen && !lastOpenState) {
        lastOpenState = true;
        onOpen();
      } else if (!nowOpen && lastOpenState) {
        lastOpenState = false;
        onClose();
      }
    });
    mo.observe(root, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'style'] });
    if (config.bodyObserve) {
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    // Initial sync in case already open on load.
    lastOpenState = !!config.isOpen(root);
    if (lastOpenState) onOpen();
  }

  function init() {
    // 1. Sidebar drawers (cart + menu). Open = classList has 'sidebar--opened'.
    document.querySelectorAll('sidebar-drawer').forEach(function (drawer) {
      attachTrap(drawer, {
        isOpen: function (el) { return el.classList.contains('sidebar--opened'); },
        close: function (el) { if (typeof el.hide === 'function') el.hide(); },
        bodyObserve: false,
        // a11-F3: only the cart drawer is opened by controls other than its
        // header icon; the menu drawer keeps its own opener logic untouched.
        returnToTrigger: drawer.id === 'site-cart-sidebar'
      });
    });

    // 2. Search overlay. Open = classList has 'is-open'.
    var searchOverlay = document.getElementById('yojo-search-overlay');
    if (searchOverlay) {
      attachTrap(searchOverlay, {
        isOpen: function (el) { return el.classList.contains('is-open'); },
        close: function (el) {
          var btn = el.querySelector('[data-search-close]');
          if (btn) btn.click();
        }
      });
    }

    // 3. Newsletter popup. Open = display != 'none' AND opacity > 0.
    var newsletter = document.getElementById('yojo-newsletter-popup');
    if (newsletter) {
      attachTrap(newsletter, {
        isOpen: function (el) {
          var cs = getComputedStyle(el);
          return cs.display !== 'none' && parseFloat(cs.opacity || '0') > 0;
        },
        close: function (el) {
          if (typeof window.closeYojoPopup === 'function') {
            window.closeYojoPopup();
          } else {
            var btn = el.querySelector('.yojo-popup__close');
            if (btn) btn.click();
          }
        }
      });
    }

    // 4. Age-verification gate (18+ modal-box). Open = classList has 'active'
    //    (managed by component-modal.js show()/hide()). HIGH-CARE: focus
    //    containment ONLY. No `close` in the config on purpose — an age gate
    //    must not be dismissible, so Escape stays disabled (mirrors the
    //    section's closeByKey:false). The approve flow ([data-js-close] click
    //    -> hide()) and the tag-based detection logic in
    //    sections/popup-age-verification.liquid are untouched.
    document.querySelectorAll('modal-box.popup-age-verification').forEach(function (gate) {
      attachTrap(gate, {
        isOpen: function (el) { return el.classList.contains('active'); }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
